import React, { useEffect, useRef, useState } from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { Dialog } from "primereact/dialog";
import { Messages } from "primereact/messages";
import { PrimeIcons } from "primereact/api";
import { BaseApiServiceImpl } from "../app_utils/api/BaseApiServiceImpl";
import { MessageUtils } from "../app_utils/utils/MessageUtils";
import { UserSessionUtils } from "../app_utils/utils/UserSessionUtils";
import CountryMarkupFormDialog from "./CountryMarkupFormDialog";

const CountryMarkupsView = () => {
  const [records, setRecords] = useState<any>([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [loading, setLoading] = useState(false);
  const [offset, setOffset] = useState(0);
  const [limit] = useState(10);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState<any>(null);
  const [deleteDialogVisible, setDeleteDialogVisible] = useState(false);
  const [recordToDelete, setRecordToDelete] = useState<any>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const message = useRef<any>();

  const fetchRecords = () => {
    setLoading(true);
    new BaseApiServiceImpl("/api/v1/country-markups")
      .getRequestWithJsonResponse({ offset, limit })
      .then(async (response) => {
        setRecords(response?.records);
        setTotalRecords(response?.totalRecords);
        setLoading(false);
      })
      .catch((error) => {
        setLoading(false);
        MessageUtils.showErrorMessage(message, error.message);
      });
  };

  useEffect(() => {
    fetchRecords();
  }, [offset]);

  const openNewDialog = () => {
    setSelectedRecord(null);
    setIsDialogOpen(true);
  };

  const openEditDialog = (record: any) => {
    setSelectedRecord({
      id: record.id,
      countryId: record.countryId,
      pricePerCredit: record.pricePerCredit,
      currencyCode: record.currencyCode,
    });
    setIsDialogOpen(true);
  };

  const confirmDelete = (record: any) => {
    setRecordToDelete(record);
    setDeleteDialogVisible(true);
  };

  const deleteRecord = () => {
    if (!recordToDelete) return;
    setIsDeleting(true);
    new BaseApiServiceImpl("/api/v1/country-markups/" + recordToDelete.id)
      .deleteRequestWithJsonResponse({
        "Content-Type": "application/json",
        Authorization: "Bearer " + (typeof UserSessionUtils !== 'undefined' ? UserSessionUtils.getBearerToken() : ""),
      })
      .then(async (response) => {
        setIsDeleting(false);
        setDeleteDialogVisible(false);
        setRecordToDelete(null);
        MessageUtils.showSuccessMessage(message, "Country markup deleted successfully");
        fetchRecords();
      })
      .catch((error) => {
        setIsDeleting(false);
        MessageUtils.showErrorMessage(message, error.message);
      });
  };

  const actionBodyTemplate = (rowData: any) => {
    return (
      <div className="flex">
        <Button
          icon={PrimeIcons.PENCIL}
          className="p-button-rounded p-button-success mr-2"
          onClick={() => openEditDialog(rowData)}
        />
        <Button
          icon={PrimeIcons.TRASH}
          className="p-button-rounded p-button-danger"
          onClick={() => confirmDelete(rowData)}
        />
      </div>
    );
  };

  const countryBodyTemplate = (rowData: any) => {
    return <span>{rowData.countryName}</span>;
  };

  const onPage = (event: any) => {
    setOffset(event.first);
  };

  const deleteDialogFooter = (
    <>
      <Button
        label="Cancel"
        icon={PrimeIcons.TIMES}
        className="p-button-text"
        onClick={() => setDeleteDialogVisible(false)}
      />
      <Button
        label="Delete"
        icon={PrimeIcons.TRASH}
        className="p-button-danger"
        onClick={deleteRecord}
        loading={isDeleting}
      />
    </>
  );

  return (
    <div className="grid">
      <div className="col-12">
        <div className="card">
          <h5>Country Markup & Price Per Credit</h5>
          <Messages ref={message} style={{ width: "100%" }} />

          <div className="flex mb-3">
            <Button
              label="Add New"
              icon={PrimeIcons.PLUS}
              className="p-button-success"
              onClick={openNewDialog}
            />
          </div>

          <DataTable
            value={records}
            lazy
            paginator
            rows={limit}
            totalRecords={totalRecords}
            loading={loading}
            onPage={onPage}
            first={offset}
            rowsPerPageOptions={[5, 10, 25, 50]}
            dataKey="id"
          >
            <Column field="countryName" header="Country" body={countryBodyTemplate} />
            <Column field="pricePerCredit" header="Price per Credit" />
            <Column field="currencyCode" header="Currency" />
            <Column body={actionBodyTemplate} headerStyle={{ width: "10rem" }} />
          </DataTable>

          <CountryMarkupFormDialog
            isOpen={isDialogOpen}
            toggle={() => setIsDialogOpen(false)}
            record={selectedRecord}
            reloadFn={fetchRecords}
            messageRef={message}
          />

          <Dialog
            visible={deleteDialogVisible}
            header="Confirm Delete"
            footer={deleteDialogFooter}
            modal
            style={{ width: "30vw" }}
            onHide={() => setDeleteDialogVisible(false)}
          >
            <p>
              Are you sure you want to delete the country markup for <b>{recordToDelete?.countryName}</b>?
              This action cannot be undone.
            </p>
          </Dialog>
        </div>
      </div>
    </div>
  );
};

export default CountryMarkupsView;
